import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { PublicInspection, PublicJobcardView } from '@garage/shared'
import { Card } from '@/ui'
import { publicApi } from '@/lib/public-api.js'
import { PublicPhotoViewer, type PhotoEntry } from './PublicPhotoViewer.js'
import { PublicInspectionPdfButton } from './PublicInspectionPdfButton.js'

/**
 * Tone-based styling for status badges.
 * Written out per tone to ensure Tailwind can statically scan the class names.
 */
const TONE_BADGE_CLASSES: Record<
  string,
  {
    container: string
    text: string
    dot: string
  }
> = {
  success: {
    container: 'bg-success-container text-on-success-container',
    text: 'text-on-success-container',
    dot: 'bg-success',
  },
  warning: {
    container: 'bg-warning-container text-on-warning-container',
    text: 'text-on-warning-container',
    dot: 'bg-warning',
  },
  error: {
    container: 'bg-error-container text-on-error-container',
    text: 'text-on-error-container',
    dot: 'bg-error',
  },
  neutral: {
    container: 'bg-surface-high text-on-surface-variant',
    text: 'text-on-surface-variant',
    dot: 'bg-surface-high',
  },
}

interface PublicInspectionSectionProps {
  token: string
  inspection: PublicInspection
  view?: PublicJobcardView
}

/**
 * Render the vehicle inspection report for the customer portal.
 * Shows the inspection summary, photos, items needing attention, and full checklist.
 * Includes a lightbox for viewing photos and a PDF download button.
 */
export function PublicInspectionSection({ token, inspection, view }: PublicInspectionSectionProps) {
  const { t } = useTranslation()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Build the flat list of photos for the lightbox (overview first, then items with photos)
  const photos = useMemo<PhotoEntry[]>(() => {
    const result: PhotoEntry[] = []
    const seenItemIds = new Set<string>()

    // Add overview photos first
    for (const photo of inspection.overview) {
      result.push({
        key: `overview-${photo.angle}`,
        url: publicApi.inspectionOverviewPhotoUrl(token, photo.angle, 1600),
        caption: t(`inspection.overviewAngles.${photo.angle}`, photo.label),
      })
    }

    // Add item photos from attention list first
    for (const section of inspection.sections) {
      for (const item of section.items) {
        if (item.hasPhoto && !seenItemIds.has(item.id) && (item.tone === 'warning' || item.tone === 'error')) {
          seenItemIds.add(item.id)
          result.push({
            key: `item-${item.id}`,
            url: publicApi.inspectionItemPhotoUrl(token, item.id, 1600),
            caption: `${section.label} — ${item.label}`,
            comment: item.comment,
          })
        }
      }
    }

    // Add remaining item photos from checklist
    for (const section of inspection.sections) {
      for (const item of section.items) {
        if (item.hasPhoto && !seenItemIds.has(item.id)) {
          seenItemIds.add(item.id)
          result.push({
            key: `item-${item.id}`,
            url: publicApi.inspectionItemPhotoUrl(token, item.id, 1600),
            caption: `${section.label} — ${item.label}`,
            comment: item.comment,
          })
        }
      }
    }

    return result
  }, [inspection, token, t])

  const openPhoto = (key: string) => {
    const idx = photos.findIndex((p) => p.key === key)
    if (idx >= 0) {
      setLightboxIndex(idx)
      setLightboxOpen(true)
    }
  }

  const scaleOptionLabel = (scaleKey: string, value: string, fallback: string) =>
    t(`inspection.scales.${scaleKey}.${value}`, fallback)

  const overviewAngleLabel = (angle: string, fallback: string) =>
    t(`inspection.overviewAngles.${angle}`, fallback)

  // Build "checked on/by" subline
  const checkedParts: string[] = []
  if (inspection.performedAt) {
    checkedParts.push(t('publicPortal.inspection.checkedOn', { date: inspection.performedAt }))
  }
  if (inspection.performedBy) {
    checkedParts.push(t('publicPortal.inspection.checkedBy', { name: inspection.performedBy }))
  }
  const checkedLine = checkedParts.length > 0 ? checkedParts.join(' · ') : null

  // Collect all items needing attention across all sections
  const attentionItems = inspection.sections.flatMap((section) =>
    section.items
      .filter((item) => item.tone === 'warning' || item.tone === 'error')
      .map((item) => ({ ...item, sectionLabel: section.label })),
  )

  return (
    <div className="space-y-4">
      {/* Photo lightbox */}
      <PublicPhotoViewer
        open={lightboxOpen}
        photos={photos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />

      <Card variant="filled">
        <div className="space-y-3">
          {/* Heading and subline with PDF button */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 flex-1">
              <h3 className="text-title-md text-on-surface">
                {t('publicPortal.inspection.heading')}
              </h3>
              {checkedLine && (
                <p className="text-body-sm text-on-surface-variant">{checkedLine}</p>
              )}
            </div>
            {view && (
              <PublicInspectionPdfButton
                garage={view.garage}
                jobcardNumber={view.jobcard.number}
                car={view.car}
                inspection={inspection}
                token={token}
              />
            )}
          </div>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-surface-high text-on-surface-variant text-label-sm">
              {t('publicPortal.inspection.itemsChecked', { count: inspection.summary.rated })}
            </div>
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-success-container text-on-success-container text-label-sm">
              {t('publicPortal.inspection.good', { count: inspection.summary.good })}
            </div>
            {inspection.summary.attention > 0 && (
              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-warning-container text-on-warning-container text-label-sm">
                {t('publicPortal.inspection.needsAttention', { count: inspection.summary.attention })}
              </div>
            )}
            {inspection.summary.attention === 0 && (
              <div className="text-body-sm text-on-surface-variant italic">
                {t('publicPortal.inspection.nothingNeedsAttention')}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Overview photos grid */}
      {inspection.overview.length > 0 && (
        <Card variant="filled">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {inspection.overview.map((photo) => (
              <OverviewPhotoThumbnail
                key={photo.angle}
                token={token}
                angle={photo.angle}
                label={overviewAngleLabel(photo.angle, photo.label)}
                onOpen={() => openPhoto(`overview-${photo.angle}`)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Needs attention section */}
      {attentionItems.length > 0 && (
        <Card variant="filled">
          <div className="space-y-3">
            <h3 className="text-title-md text-on-surface">
              {t('publicPortal.inspection.needsYourAttention')}
            </h3>
            <div className="space-y-2">
              {attentionItems.map((item) => (
                <div key={item.id} className="flex gap-3 pb-3 border-b border-outline-variant last:pb-0 last:border-0">
                  {/* Item info column */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-label-sm text-on-surface-variant mb-0.5">
                          {item.sectionLabel}
                        </p>
                        <p className="text-body-sm text-on-surface break-words">
                          {item.label}
                        </p>
                        {item.comment && (
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {item.comment}
                          </p>
                        )}
                      </div>
                      {/* Status badge on the right */}
                      <div className="shrink-0 whitespace-nowrap">
                        <StatusBadge
                          tone={item.tone}
                          label={scaleOptionLabel(item.scale, item.status, item.statusLabel)}
                        />
                      </div>
                    </div>
                    {/* The photo of a flagged item is the evidence the customer is here for. */}
                    {item.hasPhoto && (
                      <ItemPhotoThumbnail
                        token={token}
                        itemId={item.id}
                        label={item.label}
                        onOpen={() => openPhoto(`item-${item.id}`)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Full checklist */}
      <Card variant="filled">
        <div className="space-y-2">
          {inspection.sections.map((section) => {
            const sectionAttention = section.items.filter(
              (item) => item.tone === 'warning' || item.tone === 'error',
            )
            const sectionHasItems = section.items.length > 0

            return (
              <details
                key={section.id}
                className="group"
              >
                <summary className="cursor-pointer select-none py-2 px-3 hover:bg-surface-high rounded-sm list-none">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <ChevronIndicator />
                      <div className="min-w-0">
                        <p className="text-body-sm text-on-surface break-words">
                          {section.label}
                        </p>
                        {sectionHasItems && (
                          <p className="text-label-sm text-on-surface-variant">
                            {t('publicPortal.inspection.sectionCount', {
                              count: section.items.length,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    {sectionAttention.length > 0 && (
                      <div className="shrink-0 w-2 h-2 rounded-full bg-warning"></div>
                    )}
                  </div>
                </summary>

                {/* Section items */}
                <div className="ps-6 pt-2 border-s border-outline-variant space-y-2">
                  {section.items.map((item) => (
                    <div
                      key={item.id}
                      className="py-2 flex gap-3"
                    >
                      {/* Item info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-body-sm text-on-surface break-words">
                            {item.label}
                          </p>
                          <StatusBadge
                            tone={item.tone}
                            label={scaleOptionLabel(item.scale, item.status, item.statusLabel)}
                          />
                        </div>
                        {item.comment && (
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {item.comment}
                          </p>
                        )}
                        {item.hasPhoto && (
                          <ItemPhotoThumbnail
                            token={token}
                            itemId={item.id}
                            label={item.label}
                            onOpen={() => openPhoto(`item-${item.id}`)}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Section note */}
                  {section.note && (
                    <div className="py-2 px-3 bg-surface-high rounded text-body-sm text-on-surface-variant whitespace-pre-line">
                      {section.note}
                    </div>
                  )}
                </div>
              </details>
            )
          })}
        </div>
      </Card>

      {/* Overall notes */}
      {inspection.overallNotes && (
        <Card variant="filled">
          <div className="space-y-2">
            <h3 className="text-title-md text-on-surface">
              {t('publicPortal.inspection.inspectorsNotes')}
            </h3>
            <p className="text-body-sm text-on-surface-variant whitespace-pre-line">
              {inspection.overallNotes}
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}

/**
 * Status badge showing the item's status with tone-appropriate colors.
 */
function StatusBadge({
  tone,
  label,
}: {
  tone: string
  label: string
}) {
  const classes = TONE_BADGE_CLASSES[tone] || TONE_BADGE_CLASSES.neutral
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-label-sm ${classes?.container}`}>
      {label}
    </span>
  )
}

/**
 * Chevron indicator that rotates on details open.
 * Uses the `group` selector to detect when details is open.
 */
function ChevronIndicator() {
  return (
    <svg
      className="w-4 h-4 text-on-surface-variant transition-transform group-open:rotate-90"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  )
}

/**
 * A photo thumbnail that fails gracefully if the image doesn't load.
 * Hides the entire container on error.
 */
function OverviewPhotoThumbnail({
  token,
  angle,
  label,
  onOpen,
}: {
  token: string
  angle: string
  label: string
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)

  if (failed) {
    return null
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full rounded-md overflow-hidden bg-surface-high hover:bg-surface-highest transition-colors text-left"
      aria-label={t('publicPortal.inspection.openPhoto', { label })}
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={publicApi.inspectionOverviewPhotoUrl(token, angle, 320)}
          alt={label}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </div>
      <div className="px-2 py-1 text-label-sm text-on-surface">
        {label}
      </div>
    </button>
  )
}

/**
 * A small thumbnail for an inspection item photo.
 * Placed after the item comment; click opens the full image in the lightbox.
 */
function ItemPhotoThumbnail({
  token,
  itemId,
  label,
  onOpen,
}: {
  token: string
  itemId: string
  label: string
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)

  if (failed) {
    return null
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-block mt-2 rounded-md overflow-hidden bg-surface-high hover:bg-surface-highest transition-colors"
      aria-label={t('publicPortal.inspection.openPhoto', { label })}
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 overflow-hidden">
        <img
          src={publicApi.inspectionItemPhotoUrl(token, itemId, 128)}
          alt={label}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </div>
    </button>
  )
}
